/*
 * kp-pdp-tech.js — the PDP performance bands (ticket #21).
 *
 * One custom element, four jobs: keep exactly one feature open, crossfade the
 * media well to match, hydrate that feature's clips once the band is on
 * screen, and pin the nav column tall enough that the well beside it never
 * jumps.
 *
 * It is §20's core-structure contract on a different composition, so the
 * rules are the same ones: aria-expanded is the contract, arrow keys are
 * deliberately NOT bound (half a tablist is worse than none), and the markup
 * this enhances is already correct without it — the first feature is open,
 * every body is in the DOM, nothing here creates content.
 *
 * A feature is a CLUSTER of clips, not one: the hero film in the well plus up
 * to two cell films inside its body. They hydrate together and pause together,
 * because they are on screen together.
 *
 * Nothing is fetched until the band is within one screen of the viewport
 * (#43). Both instances of this section sit below the fold on a product page,
 * so hydrating on load would put two hero films and their cells on the wire
 * behind the buy box — the same bug the motion player was fixed for
 * (docs/decisions.md, 2026-08-27). Two observers: "near" (a viewport ahead)
 * hydrates the open feature's clips with preload="metadata" so the header
 * and first bytes are in before the customer arrives; "visible" is what
 * plays them, and what pauses the cluster on the way out. On the phone reel
 * the next card is warmed the same way, so a swipe lands on a clip that has
 * already started.
 *
 * A refused play() is not swallowed (#44). iOS Low Power Mode and Android's
 * Data Saver reject scripted autoplay; the well marks itself `is-blocked`,
 * which shows the play badge, and a tap on the well plays inside the gesture.
 *
 * Under prefers-reduced-motion no clip is loaded at all. These are muted loops
 * with no controls — exactly the ambient motion the setting asks us to stop,
 * and with no pause affordance to fall back on. The posters stand instead,
 * which is why the section always renders one: the picker if a merchant set
 * a frame, and the film's own preview frame if they did not.
 */
/* Wrapped, because this file is loaded once per section instance and the page
 * carries two of them: a bare top-level `const` throws "already declared" on
 * the second execution. The customElements guard at the foot stops the second
 * pass registering anything. */
(function () {
  const KP_TECH_REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Measured letterbox crops, keyed by the poster URL that was probed. Module
  // scope on purpose: a product page carries two of these sections and the two
  // bands can be cut from the same shoot, so the second one should not pay to
  // measure a frame the first one already read.
  const KP_TECH_CROP = new Map();

  class KpPdpTech extends HTMLElement {
    connectedCallback() {
      this.rows = Array.from(this.querySelectorAll('[data-kp-tech-row]'));
      this.slides = Array.from(this.querySelectorAll('[data-kp-tech-slide]'));
      this.nav = this.querySelector('[data-kp-tech-nav]');
      if (!this.rows.length) return;

      // The phone layout (#42): one card per feature in a scroll-snap reel.
      // Which layout is showing is a CSS decision at --kp-bp-stack; the
      // script reads the same query rather than measuring, so the two can
      // never disagree about which <video>s are on screen.
      this.strip = this.querySelector('[data-kp-tech-strip]');
      this.reelEl = this.querySelector('[data-kp-tech-reel]');
      this.cards = Array.from(this.querySelectorAll('[data-kp-tech-card]'));
      this.pages = Array.from(this.querySelectorAll('[data-kp-tech-page]'));
      this.stack = window.matchMedia('(max-width: 749px)');
      this.stack.addEventListener('change', this.onStack);
      this.take = this.cards.map(() => 0);

      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('resize', this.onResize);
      this.watch();

      // Selecting a block in the theme editor opens its feature, so a merchant
      // editing feature 03 is looking at feature 03. Bound on the section:
      // shopify:block:select fires on the section element, not on the block.
      this.section = this.closest('.shopify-section') || this;
      this.section.addEventListener('shopify:block:select', this.onBlockSelect);

      // The server-rendered open feature is the starting truth, not index 0 — a
      // merchant can reorder blocks and the template still marks one open.
      // Not animated: it is already open in the markup, and growing it from
      // zero on load would animate a state nothing just changed.
      const open = this.rows.findIndex((row) => row.classList.contains('is-open'));
      this.activate(open === -1 ? 0 : open, false);

      this.measure();
      // Orbitron and Rajdhani both change the height of a feature body, and the
      // first measurement usually lands before either has arrived.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.measure());
      }

      this.watchReel();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('resize', this.onResize);
      if (this.section) this.section.removeEventListener('shopify:block:select', this.onBlockSelect);
      clearTimeout(this.resizeTimer);
      if (this.observer) this.observer.disconnect();
      if (this.nearObserver) this.nearObserver.disconnect();
      if (this.reelEl) {
        this.reelEl.removeEventListener('scroll', this.onReelScroll);
        this.reelEl.removeEventListener('scrollend', this.onReelEnd);
      }
      clearTimeout(this.reelTimer);
      if (this.stack) this.stack.removeEventListener('change', this.onStack);
    }

    // Crossing --kp-bp-stack swaps which layout is on screen, so the films
    // follow: the one that just hid pauses, the one that just showed
    // hydrates. Each clip lives in a <template> per layout, so this is the
    // first fetch for the new layout and no refetch for the old.
    onStack = () => {
      this.reel();
    };

    onPointerDown = (event) => {
      this.pointerFrom = { x: event.clientX, y: event.clientY };
    };

    /*
     * A tap on a well whose film was refused plays it — inside the gesture,
     * which is what the browser was waiting for. pointerup with a travel
     * guard, not click: the well sits inside the reel's swipe surface, and a
     * swipe that merely ended on it must not start anything.
     *
     * Under prefers-reduced-motion the clip is still in its <template>; the
     * tap is the visitor asking for it, so it hydrates and plays, and reel()
     * leaves a hand-started film alone while the band is on screen.
     */
    onPointerUp = (event) => {
      const from = this.pointerFrom;
      this.pointerFrom = null;
      if (from && Math.hypot(event.clientX - from.x, event.clientY - from.y) > 10) return;

      const well = event.target.closest('.is-blocked');
      if (!well || !this.contains(well)) return;

      this.hydrate(well);
      const video = well.querySelector('video');
      if (!video) return;
      video.dataset.kpByHand = '1';
      this.play(video);
    };

    onBlockSelect = (event) => {
      const row = event.target.closest('[data-kp-tech-row]');
      if (!row || !this.contains(row)) return;
      const index = Number(row.dataset.index);
      this.activate(index);
      if (this.stack.matches) this.scrollReel(index, true);
    };

    onClick = (event) => {
      const thumb = event.target.closest('[data-kp-tech-thumb]');
      if (thumb && this.contains(thumb)) {
        const card = thumb.closest('[data-kp-tech-card]');
        this.show(Number(card.dataset.index), Number(thumb.dataset.index));
        return;
      }

      const next = event.target.closest('[data-kp-tech-next]');
      if (next && this.contains(next)) {
        this.scrollReel(Math.min(this.index + 1, this.cards.length - 1));
        return;
      }

      const toggle = event.target.closest('[data-kp-tech-toggle]');
      if (!toggle || !this.contains(toggle)) return;

      const index = Number(toggle.dataset.index);
      // Clicking the open feature does nothing. There is no "all closed" state:
      // the well would be showing media with no feature to explain it.
      if (this.rows[index] && this.rows[index].classList.contains('is-open')) return;

      this.activate(index);
    };

    // Width is what changes the measurement; a mobile browser firing resize on
    // every address-bar scroll is not.
    onResize = () => {
      if (window.innerWidth === this.lastWidth) return;
      this.lastWidth = window.innerWidth;
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.measure(), 150);
    };

    activate(index, animate = true) {
      this.rows.forEach((row, i) => {
        const isOpen = i === index;
        row.classList.toggle('is-open', isOpen);

        const toggle = row.querySelector('[data-kp-tech-toggle]');
        if (toggle && toggle.hasAttribute('aria-expanded')) {
          toggle.setAttribute('aria-expanded', String(isOpen));
        }

        this.reveal(row.querySelector('[data-kp-tech-body]'), isOpen, animate);
      });

      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });

      this.cards.forEach((card, i) => card.classList.toggle('is-current', i === index));
      this.pages.forEach((page, i) => page.classList.toggle('is-on', i === index));
      this.foot(index);

      this.index = index;
      this.reel();
    }

    /*
     * The strip's footer names the card after this one, and hides itself on
     * the last card: an arrow pointing at nothing is a broken control.
     */
    foot(index) {
      const next = this.querySelector('[data-kp-tech-next]');
      if (!next) return;
      const after = this.cards[index + 1];
      next.hidden = !after;
      if (!after) return;
      const label = next.querySelector('[data-kp-tech-next-label]');
      const title = after.getAttribute('aria-label') || '';
      if (label) label.textContent = title;
    }

    /*
     * Puts one take on a card's stage: 0 is the hero film, 1 and 2 are the
     * detail clips. The thumbnail lights, and the point that belongs to the
     * clip reads at full strength while
     * the other steps back. Only the active take's film plays — the whole
     * reason the phone gets a stage and a rail instead of three wells.
     */
    show(cardIndex, takeIndex) {
      const card = this.cards[cardIndex];
      if (!card) return;
      this.take[cardIndex] = takeIndex;

      card.querySelectorAll('[data-kp-tech-take]').forEach((take) => {
        take.classList.toggle('is-active', Number(take.dataset.index) === takeIndex);
      });
      card.querySelectorAll('[data-kp-tech-thumb]').forEach((thumb) => {
        const on = Number(thumb.dataset.index) === takeIndex;
        thumb.classList.toggle('is-on', on);
        thumb.setAttribute('aria-pressed', String(on));
      });
      card.querySelectorAll('[data-kp-tech-point]').forEach((point) => {
        point.classList.toggle('is-on', Number(point.dataset.index) === takeIndex);
      });
      card.classList.toggle('is-detail', takeIndex > 0);

      this.reel();
    }

    /*
     * Follows the reel, but only once it has stopped: the card under the
     * snap is the current feature. Settling first rather than watching
     * intersection means a card that peeks in during a swipe never hydrates
     * its film half-way through the gesture — the fetch and the play both
     * wait for the snap to land. scrollend where the browser has it; a
     * 120ms quiet period otherwise.
     */
    watchReel() {
      if (!this.reelEl) return;
      const settle = () => {
        clearTimeout(this.reelTimer);
        this.reelTimer = null;
        this.onReelSettled();
      };
      this.onReelScroll = () => {
        clearTimeout(this.reelTimer);
        this.reelTimer = setTimeout(settle, 120);
      };
      this.onReelEnd = () => settle();
      this.reelEl.addEventListener('scroll', this.onReelScroll, { passive: true });
      this.reelEl.addEventListener('scrollend', this.onReelEnd);
    }

    onReelSettled() {
      if (!this.cards.length) return;
      const origin = this.cards[0].offsetLeft;
      const pitch = this.cards.length > 1 ? this.cards[1].offsetLeft - origin : 1;
      const index = Math.max(0, Math.min(this.cards.length - 1, Math.round(this.reelEl.scrollLeft / pitch)));
      if (index !== this.index) this.activate(index);
    }

    scrollReel(index, silent = false) {
      const card = this.cards[index];
      if (!card || !this.reelEl) return;
      // Relative to the first card, which sits at the snap origin: the reel
      // bleeds past the container by its own padding, so its offsetLeft is
      // not where a snapped card lands.
      this.reelEl.scrollTo({
        left: card.offsetLeft - this.cards[0].offsetLeft,
        behavior: silent || KP_TECH_REDUCED.matches ? 'auto' : 'smooth',
      });
    }

    /*
     * Opens or closes one feature body, growing it between zero and its own
     * measured height.
     *
     * `hidden` stays the contract — it is what the markup ships with, what a
     * screen reader reads, and what the no-JS page relies on — so the
     * animation brackets it rather than replacing it: the attribute comes off
     * before an open and goes back on after a close, and display: none is
     * never on screen while the height is moving.
     *
     * Measured, not guessed, and measured at the moment of the toggle: the
     * copy is merchant-set and the cells rewrap at every width, so the only
     * honest number is the one the box has right now.
     */
    reveal(body, open, animate) {
      if (!body) return;

      this.settle(body);

      // The instant path is not just an optimisation. A transition killed by
      // prefers-reduced-motion never fires transitionend, so the animated path
      // would strand an inline height on the box and leave it that size for
      // good.
      if (!animate || KP_TECH_REDUCED.matches) {
        body.hidden = !open;
        return;
      }

      const from = body.hidden ? 0 : body.scrollHeight;
      if (open) body.hidden = false;
      const to = open ? body.scrollHeight : 0;

      if (from === to) {
        body.hidden = !open;
        return;
      }

      body.style.height = `${from}px`;
      // Read it back, so the browser has a start value to animate away from
      // rather than collapsing both writes into one style resolution.
      void body.offsetHeight;
      body.style.height = `${to}px`;

      body.kpEnd = (event) => {
        // The cells inside carry their own transitions; only this box's own
        // height ends the reveal.
        if (event.target !== body || event.propertyName !== 'height') return;
        this.settle(body);
        if (!open) body.hidden = true;
      };
      body.addEventListener('transitionend', body.kpEnd);
    }

    // Puts a body back on auto height and drops any listener still waiting on
    // it. Called before every reveal and by measure(), so a toggle that lands
    // mid-animation replaces the old one instead of racing it.
    settle(body) {
      if (!body) return;
      if (body.kpEnd) {
        body.removeEventListener('transitionend', body.kpEnd);
        body.kpEnd = null;
      }
      body.style.height = '';
    }

    /*
     * Watches for the band reaching the viewport. Everything about the films
     * hangs off it: nothing is fetched before the customer can see the band,
     * and the cluster pauses on the way out — an off-screen autoplaying video
     * still decodes every frame, and a product page carries two of these.
     */
    watch() {
      // No observer to gate on: treat the band as always visible and let
      // reel() apply the reduced-motion rule on its own. It used to return
      // early here, which left this.visible undefined — so on a browser with
      // no IntersectionObserver, reel() read that as off-screen and paused
      // every film forever.
      if (!('IntersectionObserver' in window)) {
        this.visible = true;
        this.near = true;
        this.crop();
        this.reel();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.visible = entry.isIntersecting;
          this.reel();
        });
      });
      this.observer.observe(this);

      // One viewport ahead: hydrate and warm, never play. The probes ride on
      // it too — they are small, and reading the frame a screen early means
      // the crop is on before the band is seen.
      this.nearObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.near = entry.isIntersecting;
            if (entry.isIntersecting) this.crop();
            this.reel();
          });
        },
        { rootMargin: '100% 0px' }
      );
      this.nearObserver.observe(this);
    }

    /*
     * Some of the source films ship with black bars baked INTO the picture —
     * a wide render exported onto a 16:9 canvas by an editor who is no longer
     * around to re-export it. object-fit: cover cannot help: the file really
     * is 16:9, and the bars are part of the frame rather than around it. The
     * only fix is to scale the picture up until they are outside the well.
     *
     * Measured off the POSTER, never the film. The poster is a frame of the
     * same film so it carries the same bars, and a 200px-wide copy is a few
     * kilobytes. Reading the film itself would mean a second fetch of a video
     * AND crossorigin="anonymous" on the element that actually plays it —
     * which turns a CORS header we do not control into a black well the day it
     * goes missing. Here the worst case is a probe that fails to load, and the
     * band keeps its bars.
     *
     * The crop lands on the poster as well as the film. They are the same
     * frame, and under prefers-reduced-motion the poster is the whole section
     * — that visitor would otherwise be the only one left looking at bars.
     *
     * Hero wells only, and the cells are not an oversight. A cell well is 2:1
     * and the sources are 16:9, so object-fit: cover already crops about 5.6%
     * off each side of a cell — which is the size of the bars. Cropping them
     * again would eat the picture.
     */
    crop() {
      if (this.cropped) return;
      this.cropped = true;

      this.querySelectorAll('[data-kp-tech-probe]').forEach((well) => {
        const url = well.dataset.kpTechProbe;
        if (!url) return;

        if (KP_TECH_CROP.has(url)) {
          this.paint(well, KP_TECH_CROP.get(url));
          return;
        }

        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.decoding = 'async';
        probe.addEventListener('load', () => {
          const bars = this.bars(probe);
          KP_TECH_CROP.set(url, bars);
          this.paint(well, bars);
        });
        // Not worth reporting. A band with its bars is the state this section
        // has always shipped in; it is just not as tight.
        probe.addEventListener('error', () => KP_TECH_CROP.set(url, null));
        probe.src = url;
      });
    }

    /*
     * Walks in from the top and bottom edges of the poster while the rows are
     * black, and returns the transform that pushes those rows out of the well.
     *
     * The guards are the whole method. A dark shot is not a letterbox, and
     * scaling one up would crop a real picture for no reason — so bars are
     * only believed when they are thin (under 14% a side), roughly equal
     * (within 3 points of each other), present at all (over 1.2% together)
     * and leave most of the frame behind (72%). Anything else returns null and
     * the film is left exactly as the merchant uploaded it.
     *
     * Thresholds and arithmetic are the canvas's, unchanged — they were tuned
     * against these particular files.
     */
    bars(image) {
      const width = 48;
      // The poster's own row count, so the read is not upsampled into
      // precision it does not have. Clamped: enough rows to resolve a bar to
      // about a percent, few enough to stay a cheap read.
      const height = Math.max(60, Math.min(240, image.naturalHeight || 160));

      let pixels;
      try {
        const scratch = document.createElement('canvas');
        scratch.width = width;
        scratch.height = height;
        const ctx = scratch.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(image, 0, 0, width, height);
        pixels = ctx.getImageData(0, 0, width, height).data;
      } catch (error) {
        // Tainted canvas: the CDN did not honour the crossorigin request.
        return null;
      }

      const dark = (row) => {
        let sum = 0;
        for (let i = row * width * 4; i < (row + 1) * width * 4; i += 4) {
          sum += pixels[i] + pixels[i + 1] + pixels[i + 2];
        }
        return sum / (width * 3) < 7;
      };

      let top = 0;
      let bottom = 0;
      while (top < height && dark(top)) top += 1;
      while (bottom < height - top && dark(height - 1 - bottom)) bottom += 1;

      const t = top / height;
      const b = bottom / height;
      const picture = 1 - t - b;

      if (picture < 0.72 || t + b < 0.012 || t > 0.14 || b > 0.14 || Math.abs(t - b) > 0.03) {
        return null;
      }

      return {
        // The extra 0.004 is a hair of overscan, so a bar that ends mid-pixel
        // does not leave a grey line along the edge of the well.
        scale: Number((1 / picture + 0.004).toFixed(4)),
        // Recentres when the two bars are not equal. Negative when the top bar
        // is the thicker one, which is the direction that lifts the picture.
        shift: Number((((b - t) / 2) * 100).toFixed(2)),
      };
    }

    paint(well, bars) {
      if (!bars) return;
      well.style.setProperty('--kp-tech-crop-s', bars.scale);
      well.style.setProperty('--kp-tech-crop-dy', `${bars.shift}%`);
      well.classList.add('is-cropped');
    }

    /*
     * Brings the films in line with what is open and what is on screen. One
     * method rather than two, so there is no order of events in which a
     * feature ends up open, visible and still paused.
     */
    reel() {
      const videos = Array.from(this.querySelectorAll('video'));

      if (!this.visible && !this.near) {
        videos.forEach((video) => video.pause());
        return;
      }

      // Reduced motion requests nothing on its own. A film the visitor
      // started by hand is theirs, and keeps going while the band is on
      // screen; off screen it pauses like any other.
      if (KP_TECH_REDUCED.matches) {
        videos.forEach((video) => {
          if (!(video.dataset.kpByHand && this.visible)) video.pause();
        });
        // The badge is that visitor's only sign a film exists, and the tap
        // their only way in — so every well still holding a clip is marked.
        this.querySelectorAll('[data-kp-tech-video]').forEach((template) => {
          template.parentElement.classList.add('is-blocked');
        });
        return;
      }

      const stacked = this.stack.matches;
      const cluster = stacked ? this.stageTake(this.index) : this.cluster(this.index);
      cluster.forEach((node) => this.hydrate(node));

      // Warm the card after this one: its clip leaves the <template> and
      // fetches its header, and nothing more, until the reel lands on it.
      if (stacked) this.stageTake(this.index + 1).forEach((node) => this.hydrate(node));

      // Everything that is not the cluster pauses — the other layout's clips
      // included, which is what makes crossing the breakpoint safe. Near but
      // not yet visible, nothing plays at all.
      const playing = new Set(this.visible ? cluster.flatMap((node) => Array.from(node.querySelectorAll('video'))) : []);
      this.querySelectorAll('video').forEach((video) => (playing.has(video) ? this.play(video) : video.pause()));
    }

    // The hero slide and the feature's own body: everything that shows when
    // this feature is open.
    cluster(index) {
      return [this.slides[index], this.rows[index]].filter(Boolean);
    }

    // The phone's answer to cluster(): the one take on the current card's
    // stage. Never the thumbnails, which are stills.
    stageTake(index) {
      const card = this.cards[index];
      if (!card) return [];
      const take = card.querySelector(`[data-kp-tech-take][data-index="${this.take[index] || 0}"]`);
      return take ? [take] : [];
    }

    // Moves each <video> out of its <template> the first time its feature opens,
    // so a clip nobody looks at is never requested. Runs once per template: the
    // template is removed with it.
    hydrate(node) {
      // A well may hold two templates — landscape and portrait (#40). Which
      // one becomes real is decided here, once, from the viewport at first
      // open, and the other is dropped: swapping on rotate would refetch a
      // clip mid-read, and <source media> is not re-evaluated either.
      const portrait = window.matchMedia('(max-width: 749px)').matches;

      node.querySelectorAll('[data-kp-tech-video]').forEach((template) => {
        const well = template.parentElement;
        const kind = template.dataset.kpTechVideo || 'landscape';
        const sibling = Array.from(well.querySelectorAll(':scope > [data-kp-tech-video]')).find((t) => t !== template);
        const wanted = portrait ? 'portrait' : 'landscape';

        if (sibling && kind !== wanted) {
          template.remove();
          return;
        }

        well.appendChild(template.content.cloneNode(true));
        template.remove();

        const video = well.querySelector('video');
        if (!video) return;

        // video_tag writes autoplay/loop/muted, but a cloned node can reach the
        // document with muted unset, and an unmuted autoplay is refused.
        video.muted = true;
        // The snippet ships preload="none" so a <template> costs nothing;
        // once it is real, the header may come in. play() fetches the rest.
        video.preload = 'metadata';
        video.addEventListener('playing', () => {
          const blocked = video.closest('.is-blocked');
          if (blocked) blocked.classList.remove('is-blocked');
        });
      });
    }

    /*
     * A rejected promise used to be swallowed here. It still is not an
     * error — the poster is a correct frame — but the well now says the film
     * is waiting for a tap, because on a phone in Low Power Mode nothing else
     * on this band would ever tell the visitor a film exists.
     */
    play(video) {
      const played = video.play();
      if (!played || typeof played.catch !== 'function') return;
      played.catch((error) => {
        if (error && error.name === 'AbortError') return;
        const well = video.closest('[data-kp-tech-take], [data-kp-tech-slide], .kp-tech__cell-well');
        if (well) well.classList.add('is-blocked');
      });
    }

    /*
     * Pins the nav column to the height of its TALLEST feature, so the 74%-wide
     * well beside it keeps one height whichever feature is open. Without it the
     * whole frame grows and shrinks under the cursor as a customer reads down
     * the list, which on a 16:9 well is a visible jolt.
     *
     * Measured rather than guessed: the copy is merchant-set and the cells wrap
     * differently at every width, so there is no number to hard-code. The CSS
     * only applies the value above the column break — below it the features
     * stack and the pin would be dead space.
     */
    measure() {
      if (!this.nav || this.stack.matches) return;

      const bodies = this.rows.map((row) => row.querySelector('[data-kp-tech-body]'));
      const open = this.rows.findIndex((row) => row.classList.contains('is-open'));

      // A body mid-reveal is holding an inline height, and this method reads
      // scrollHeight through exactly that box. Settling first also cancels the
      // pending transitionend, which would otherwise fire during the sweep
      // below and re-hide a body this method had just opened to measure it.
      bodies.forEach((body) => this.settle(body));

      this.style.removeProperty('--kp-tech-nav-min');

      let tallest = 0;
      bodies.forEach((_, i) => {
        bodies.forEach((body, j) => {
          if (body) body.hidden = j !== i;
        });
        tallest = Math.max(tallest, this.nav.scrollHeight);
      });

      bodies.forEach((body, j) => {
        if (body) body.hidden = j !== open;
      });

      if (tallest) this.style.setProperty('--kp-tech-nav-min', `${Math.ceil(tallest)}px`);
    }
  }

  if (!customElements.get('kp-pdp-tech')) {
    customElements.define('kp-pdp-tech', KpPdpTech);
  }
})();
